import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import toast from 'react-hot-toast';

const PLATFORM_STYLE = {
  indeed: ['Indeed', 'fa-brands fa-indeed', 'bg-blue-100 text-blue-700 border-blue-200'],
  linkedin: ['LinkedIn', 'fa-brands fa-linkedin', 'bg-sky-100 text-sky-700 border-sky-200'],
  google: ['Google Jobs', 'fa-brands fa-google', 'bg-amber-100 text-amber-700 border-amber-200'],
  ziprecruiter: ['ZipRecruiter', 'fa-solid fa-bolt', 'bg-emerald-100 text-emerald-700 border-emerald-200'],
  facebook: ['Facebook', 'fa-brands fa-facebook', 'bg-indigo-100 text-indigo-700 border-indigo-200'],
  referral: ['Referral', 'fa-solid fa-user-group', 'bg-violet-100 text-violet-700 border-violet-200'],
  careers: ['Careers page', 'fa-solid fa-globe', 'bg-slate-100 text-slate-600 border-slate-200'],
};
const ALIAS = { li: 'linkedin', googlejobs: 'google', google_jobs: 'google', zip: 'ziprecruiter', zr: 'ziprecruiter', fb: 'facebook' };

export function platformOf(c) {
  if (!c.platform && !c.channel) return null;
  const raw = (c.platform || c.channel || '').toLowerCase();
  const key = ALIAS[raw] || raw;
  const [label, icon, cls] = PLATFORM_STYLE[key] || [c.platform_label || raw, 'fa-solid fa-link', 'bg-slate-100 text-slate-600 border-slate-200'];
  return { key, label, icon, cls };
}

export function PlatformTag({ candidate, size = 'sm' }) {
  const p = platformOf(candidate);
  if (!p) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-bold uppercase ${p.cls} ${size === 'sm' ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2 py-0.5'}`} title={`Applied via ${p.label} tracking link`} data-testid={`platform-tag-${p.key}`}>
      <i className={p.icon} />{p.label}
    </span>
  );
}

const fmtAnswer = (a) => {
  const v = a?.answer;
  if (v === true || v === 'true') return 'Yes';
  if (v === false || v === 'false') return 'No';
  return v === undefined || v === null || String(v).trim() === '' ? '' : String(v);
};

export function ApplicationAnswers({ candidate }) {
  const answers = candidate.screening_answers || [];
  const yn = (v) => (v ? 'Yes' : 'No');
  const profile = [
    ['Years of sales experience', candidate.years_experience],
    ['Closing rate %', candidate.closing_rate],
    ['Solar/renewable experience', yn(candidate.solar_experience)],
    ['Door-to-door experience', yn(candidate.door_to_door)],
    ['Reliable vehicle', yn(candidate.has_vehicle)],
    ['Bilingual', yn(candidate.bilingual)],
    ['Full-time availability', yn(candidate.full_time)],
  ];
  return (
    <div className="bg-surface-alt rounded-xl p-3 space-y-3" data-testid="application-answers">
      <span className="text-[10px] font-bold uppercase text-text-muted"><i className="fa-solid fa-clipboard-list mr-1" />Application responses</span>
      {answers.length > 0 ? (
        <div className="space-y-2" data-testid="screening-answers-list">
          {answers.map((a, i) => (
            <div key={a.question_id || i} className="bg-surface border border-border rounded-lg px-2.5 py-2" data-testid={`screening-answer-${i}`}>
              <p className="text-[11px] font-semibold text-text-secondary">{a.question || a.question_id}</p>
              <p className={`text-xs mt-0.5 whitespace-pre-wrap ${fmtAnswer(a) ? 'text-text-primary' : 'text-text-muted italic'}`}>{fmtAnswer(a) || 'Not answered'}</p>
            </div>
          ))}
        </div>
      ) : <p className="text-[11px] text-text-muted">No screening questions were answered.</p>}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        {profile.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2 border-b border-border/50 py-0.5"><span className="text-text-muted">{k}</span><span className="font-semibold text-text-primary">{v === '' || v == null ? '—' : String(v)}</span></div>
        ))}
      </div>
      {candidate.notes && <div><p className="text-[10px] font-bold uppercase text-text-muted">Why they're a great fit</p><p className="text-xs text-text-secondary whitespace-pre-wrap mt-0.5">{candidate.notes}</p></div>}
    </div>
  );
}

export function AiCriteriaModal({ onClose }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/api/hiring/ai-criteria').then(r => { setData(r.data); setText(r.data.is_default ? '' : r.data.criteria); }).catch(() => toast.error('Could not load AI criteria'));
  }, []);
  const save = async (value) => {
    setSaving(true);
    try {
      const r = await api.put('/api/hiring/ai-criteria', { criteria: value });
      setData(d => ({ ...d, ...r.data })); setText(r.data.is_default ? '' : r.data.criteria);
      toast.success(value ? 'AI review criteria saved' : 'Reset to the default criteria');
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="ai-criteria-modal">
      <div className="bg-surface rounded-2xl border border-border max-w-xl w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div>
          <h3 className="font-heading text-lg font-bold text-text-primary"><i className="fa-solid fa-robot mr-2 text-primary" />AI review criteria</h3>
          <p className="text-xs text-text-muted mt-1">Run AI Review scores every applicant against this standard, using their screening answers and resume. A job can override it with its own criteria (Jobs &amp; Postings → job → Edit questions &amp; fields).</p>
        </div>
        <textarea rows={8} value={text} onChange={e => setText(e.target.value)} placeholder={data?.default || 'Loading…'} className="input-field text-sm" data-testid="ai-criteria-input" />
        {data && <p className="text-[10px] text-text-muted">{data.is_default ? 'Using the default criteria (shown as placeholder).' : `Custom criteria${data.updated_by ? ` · last set by ${data.updated_by}` : ''}`}</p>}
        <div className="flex justify-between gap-2">
          <button onClick={() => save('')} disabled={saving} className="btn-secondary text-xs" data-testid="ai-criteria-reset-btn">Reset to default</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-secondary text-xs">Cancel</button>
            <button onClick={() => save(text.trim())} disabled={saving || !text.trim()} className="btn-primary text-xs" data-testid="ai-criteria-save-btn">{saving ? <i className="fa-solid fa-circle-notch fa-spin mr-1" /> : <i className="fa-solid fa-check mr-1" />}Save criteria</button>
          </div>
        </div>
      </div>
    </div>
  );
}
